import {
	createSlice,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import { getFormApproval, getLeaveReport, updateFormIsRostered } from "api";

export const fetchLeaveReport = createAsyncThunk(
	"formApproval/fetchLeaveReport",
	async ({ leaveFrom, leaveTo }, { rejectWithValue, dispatch }) => {
		try {
			const response = await getLeaveReport({
				leaveFrom,
				leaveTo,
			});
			dispatch(leaveReportReceived(response));
			return true;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const fetchFormApproval = createAsyncThunk(
	"formApproval/fetchFormApproval",
	async (
		{
			approverId,
			personId,
			workflowWaitingOnId,
			formTypeId,
			includeHistory,
		},
		{ rejectWithValue, dispatch }
	) => {
		try {
			const response = await getFormApproval({
				approverId,
				personId,
				workflowWaitingOnId,
				formTypeId,
				includeHistory,
			});
			dispatch(formApprovalReceived(response));
			return true;
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

export const postIsRostered = createAsyncThunk(
	"formApproval/postIsRostered",
	async ({ id, isRostered }, { rejectWithValue, dispatch }) => {
		try {
			await updateFormIsRostered({
				formGuid: id,
				isRostered,
			});
			return { id, isRostered };
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const initialState = {
	status: "idle",
	error: null,
	data: [],
	leaveReportData: [],
};

const formApprovalSlice = createSlice({
	name: "formApproval",
	initialState,
	reducers: {
		formApprovalReceived(state, action) {
			state.data = action.payload;
		},
		leaveReportReceived(state, action) {
			state.leaveReportData = action.payload;
		},
	},
	extraReducers: {
		[fetchFormApproval.pending]: (state, action) => {
			state.status = "loading";
			state.error = null;
		},
		[fetchFormApproval.fulfilled]: (state, action) => {
			state.status = "idle";
			state.error = null;
		},
		[fetchFormApproval.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[fetchLeaveReport.pending]: (state, action) => {
			state.status = "loading";
			state.error = null;
		},
		[fetchLeaveReport.fulfilled]: (state, action) => {
			state.status = "idle";
			state.error = null;
		},
		[fetchLeaveReport.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postIsRostered.pending]: (state, action) => {
			state.error = null;
		},
		[postIsRostered.fulfilled]: (state, action) => {
			const { id, isRostered } = action.payload;
			const form = state.data.find((form) => form.id === id);
			form.isRostered = isRostered;
			state.error = null;
		},
		[postIsRostered.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
	},
});

export const { formApprovalReceived, leaveReportReceived } =
	formApprovalSlice.actions;

export const selectFormApproval = (state) => state.cme.formApproval.data;

export const selectLeaveReport = (state) =>
	state.cme.formApproval.leaveReportData;

export const selectFormApprovalWaitingOn = createSelector(
	[selectFormApproval],
	(data) => {
		let response = [];
		data.forEach((d) => {
			if (!response.some((r) => r === d.workflowWaitingOnId))
				response.push(d.workflowWaitingOnId);
		});
		return response
			.map((r) => {
				let record = data.find((d) => d.workflowWaitingOnId === r);
				return {
					id: record.workflowWaitingOnId,
					fullName: record.workflowWaitingOn,
				};
			})
			.sort((a, b) =>
				a.fullName < b.fullName ? -1 : a.fullName > b.fullName ? 1 : 0
			);
	}
);

export const selectFormApprovalFrom = createSelector(
	[selectFormApproval],
	(data) => {
		let response = [];
		data.forEach((d) => {
			if (!response.some((r) => r === d.personId))
				response.push(d.personId);
		});
		return response
			.map((r) => {
				let record = data.find((d) => d.personId === r);
				return {
					id: record.personId,
					fullName: record.personName,
				};
			})
			.sort((a, b) =>
				a.fullName < b.fullName ? -1 : a.fullName > b.fullName ? 1 : 0
			);
	}
);

export const selectFormApprovalStatus = (state) =>
	state.cme.formApproval.status;
export const selectFormApprovalError = (state) => state.cme.formApproval.error;

export default formApprovalSlice.reducer;
